import { defineStore } from 'pinia'
import { router } from '@inertiajs/vue3'
import axios from 'axios'

/**
 * Members-area header state.
 *
 * Phase 0 of the Blade→Vue header migration (NEX-9195). Holds the server-resolved
 * props (member tier, access flags, pre-resolved route URLs — see
 * app/DTO/Members/MembersHeaderData.php) plus the UI-only interaction state that
 * later phases will own (which dropdown is open, mobile menu, search focus).
 *
 * Hydrated once from window.__membersHeaderProps via initFromProps(); makes zero
 * API calls on first load, mirroring the admin daily-report pattern.
 */

// Push-down ad config (see MembersHeaderViewData::buildPushDownAd).
function normalizePushDownAd(pushDownAd = {}) {
    return {
        eligible: Boolean(pushDownAd?.eligible),
        desktopZone: Number(pushDownAd?.desktopZone ?? 0),
        mobileZone: Number(pushDownAd?.mobileZone ?? 0),
        adFrequency: Number(pushDownAd?.adFrequency ?? 1),
        isTrial: Boolean(pushDownAd?.isTrial),
        slot: String(pushDownAd?.slot ?? ''),
        userId: String(pushDownAd?.userId ?? ''),
    }
}

export const useMembersHeaderStore = defineStore('membersHeader', {
    state: () => ({
        // --- Server-resolved props (filled by initFromProps) ---
        memberTier: '',
        isGold: false,
        isTrial: false,
        isExpired: false,
        isAccessMember: false,
        isOculus: false,
        isHeresphere: false,
        membershipTier: 0,
        alertCount: 0,
        // --- Star Rewards (redesign) ---
        headerRedesign: false,
        starBalance: 0,
        starsAbbrev: '0',
        starImg: '',
        // Membership context for LTIAnalytics tracking (membership_*() helpers).
        // users.favorite_notification — gates the favourites popup (C18-A6).
        favoriteNotification: false,
        favoritesCloseIcon: '',
        // get_user_device() — the `platform` dimension on Searched Scene Tracked.
        platform: '',
        membershipLifecycleStage: '',
        fyfExperience: '',
        membershipTerm: '',
        membershipTierLabel: '',
        isHeadset: false,
        watchCount: 0,

        // --- Static header chrome (Stage A: full-Vue header shell) ---
        blueLoaderUrl: '',
        // white / whiteWebp are the mobile-menu brand bar's wordmark (.mm-brand).
        // This object is copied key-by-key on hydrate, so a key added to the DTO
        // and not added here silently arrives as undefined — which renders as a
        // broken <img>, not as an error.
        logo: { webp: '', webpDark: '', png: '', pngDark: '', white: '', whiteWebp: '' },
        headsetSearch: { openIcon: '', closeIcon: '' },
        liveGirls: { url: '', consentGated: false },

        navLinks: [],
        mobileMenuItems: [],
        countdown: null,
        upgradeUrl: '',
        routes: {},
        csrfToken: '',
        searchTerm: '',

        // --- Search region (Phase 4) ---
        // Pre-resolved endpoint URLs (Ziggy unavailable in members3).
        autocompleteUrl: '',
        searchActionUrl: '',
        // True only when the header is mounted INSIDE the running Inertia SPA shell
        // (MembersAreaLayout calls markSpaActive()). The standalone Blade-island
        // header (header.js) never sets it, because Inertia's router isn't
        // initialised there — so search must use native navigation. NOT derived
        // from config('members.spa'): that's true on Blade pages too, where the
        // island runs and router.get() would have no Inertia app to drive.
        spaActive: false,
        trendingAuditionsUrl: '',
        trendingTagNextUrl: '',
        auditionsFeedUrl: '',
        // Server-preloaded trending data for the auditions dropdown.
        trendingAuditions: [],
        trendingSearches: [],
        // Naughty America AI section of the trending dropdown (NEX-9384) — same data as the
        // Blade dropdown (TrendingAuditionsComposer::buildAiSearch). promos empty = not shown.
        aiSearch: { title: '', heading: '', url: null, promos: [] },

        // --- Footer (Phase 5) ---
        // Server-resolved footer content (Ziggy unavailable in members3).
        footerMenus: [],
        socialLinks: [],
        copyrightHtml: '',
        legalLinks: [],
        processingAddress: { paymentAgentDisclaimer: '', processorLines: [] },
        ageVerifyUrl: '',
        rtaImageUrl: '',

        // --- Category chip rail (#custom-tags content) ---
        // Server-resolved chip data (URLs pre-resolved; Ziggy unavailable here).
        customTags: {
            allTagUrl: '',
            allTagTrackLocation: 'Search Results Header',
            allTagTrackName: 'All',
            arrowLeftUrl: '',
            arrowRightUrl: '',
            selectedTag: '---',
            showController: false,
            tags: [],
            // { href, label, trackName, trackLocation } | null — the compact header's "AI" pill.
            aiPill: null,
        },

        // Push-down ad config (see MembersHeaderViewData::buildPushDownAd).
        pushDownAd: {
            eligible: false,
            desktopZone: 0,
            mobileZone: 0,
            adFrequency: 1,
            isTrial: false,
            slot: '',
            userId: '',
        },

        // Autocomplete state. Mirrors the legacy module-scope vars:
        //   membersAutocompleteItems / membersSearchTerm / membersLastRequestTerm.
        // grouped = { Category, Site, Pornstar, AI } for the current term — AI is develop's
        // Naughty America AI group (NEX-9384, type `na_ai`), rendered last.
        searchResults: [], // raw items returned by the API (post VR filter)
        searchGrouped: { Category: [], Site: [], Pornstar: [], AI: [] },
        searchActiveTerm: '', // term whose results are currently shown
        searchLastRequestTerm: '', // last term we fired a request for (stale guard)
        searchEmpty: false, // true → render the empty / VR-empty state
        vrFilterActive: false,

        // NOTE: transient UI chrome (openDropdown, mobileMenuOpen, nav skeleton)
        // now lives in @/store/useMembersUiStore — this store holds server DATA only.

        // --- Plumbing flag so we can assert hydration in dev ---
        hydrated: false,
    }),

    getters: {
        route: state => name => state.routes[name] ?? null,
        hasSearchResults: state =>
            state.searchGrouped.Category.length > 0
            || state.searchGrouped.Site.length > 0
            || state.searchGrouped.Pornstar.length > 0
            || state.searchGrouped.AI.length > 0,

        // Membership dimensions to spread into LTIAnalytics.track payloads, matching
        // the legacy Blade tracking ([Tracking] More links and ads now have
        // membership info). Spread: { ...store.membershipTracking }.
        membershipTracking: state => ({
            membership_lifecycle_stage: state.membershipLifecycleStage,
            fyf_experience: state.fyfExperience,
            membership_term: state.membershipTerm,
            membership_tier: state.membershipTierLabel,
        }),
    },

    actions: {
        // Live-update the header star balance after a star purchase (e.g. the
        // Discount Shop Bar). Mirrors the legacy headerRedesign.blade
        // window.rdSetStarBalance + formatStars() abbreviation so the SPA header
        // reflects the new balance without a full navigation.
        setStarBalance(n) {
            const balance = Number(n) || 0
            this.starBalance = balance
            this.starsAbbrev = this.formatStars(balance)
        },

        formatStars(n) {
            if (n < 1000) return String(n)
            if (n < 1000000) {
                if (n < 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
                return Math.round(n / 1000) + 'K'
            }
            const m = n / 1000000
            return (m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(/\.0$/, '')) + 'M'
        },

        initFromProps(props = {}) {
            Object.assign(this, {
                memberTier: props.memberTier ?? '',
                isGold: Boolean(props.isGold),
                isTrial: Boolean(props.isTrial),
                isExpired: Boolean(props.isExpired),
                isAccessMember: Boolean(props.isAccessMember),
                isOculus: Boolean(props.isOculus),
                isHeresphere: Boolean(props.isHeresphere),
                membershipTier: Number(props.membershipTier ?? 0),
                alertCount: Number(props.alertCount ?? 0),
                headerRedesign: Boolean(props.headerRedesign),
                starBalance: Number(props.starBalance ?? 0),
                starsAbbrev: props.starsAbbrev ?? '0',
                starImg: props.starImg ?? '',
                favoriteNotification: Boolean(props.favoriteNotification),
                favoritesCloseIcon: props.favoritesCloseIcon ?? '',
                platform: props.platform ?? '',
                membershipLifecycleStage: props.membershipLifecycleStage ?? '',
                fyfExperience: props.fyfExperience ?? '',
                membershipTerm: props.membershipTerm ?? '',
                membershipTierLabel: props.membershipTierLabel ?? '',
                isHeadset: Boolean(props.isHeadset),
                watchCount: Number(props.watchCount ?? 0),

                // --- Static header chrome (Stage A: full-Vue header shell) ---
                blueLoaderUrl: props.blueLoaderUrl ?? '',
                logo: {
                    webp: props.logo?.webp ?? '',
                    webpDark: props.logo?.webpDark ?? '',
                    png: props.logo?.png ?? '',
                    pngDark: props.logo?.pngDark ?? '',
                    white: props.logo?.white ?? '',
                    whiteWebp: props.logo?.whiteWebp ?? '',
                },
                headsetSearch: {
                    openIcon: props.headsetSearch?.openIcon ?? '',
                    closeIcon: props.headsetSearch?.closeIcon ?? '',
                },
                liveGirls: {
                    url: props.liveGirls?.url ?? '',
                    consentGated: Boolean(props.liveGirls?.consentGated),
                },

                navLinks: Array.isArray(props.navLinks) ? props.navLinks : [],
                mobileMenuItems: Array.isArray(props.mobileMenuItems) ? props.mobileMenuItems : [],
                countdown: props.countdown ?? null,
                upgradeUrl: props.upgradeUrl ?? '',
                routes: props.routes ?? {},
                csrfToken: props.csrfToken ?? '',
                searchTerm: props.searchTerm ?? '',

                // --- Search region (Phase 4) ---
                autocompleteUrl: props.autocompleteUrl ?? '',
                searchActionUrl: props.searchActionUrl ?? '',
                trendingAuditionsUrl: props.trendingAuditionsUrl ?? '',
                trendingTagNextUrl: props.trendingTagNextUrl ?? '',
                auditionsFeedUrl: props.auditionsFeedUrl ?? '',
                trendingAuditions: Array.isArray(props.trendingAuditions) ? props.trendingAuditions : [],
                trendingSearches: Array.isArray(props.trendingSearches) ? props.trendingSearches : [],
                aiSearch: {
                    title: props.aiSearch?.title ?? '',
                    heading: props.aiSearch?.heading ?? '',
                    url: props.aiSearch?.url ?? null,
                    promos: Array.isArray(props.aiSearch?.promos) ? props.aiSearch.promos : [],
                },
                vrFilterActive: Boolean(props.vrFilterActive),

                // --- Footer (Phase 5) ---
                footerMenus: Array.isArray(props.footerMenus) ? props.footerMenus : [],
                socialLinks: Array.isArray(props.socialLinks) ? props.socialLinks : [],
                copyrightHtml: props.copyrightHtml ?? '',
                legalLinks: Array.isArray(props.legalLinks) ? props.legalLinks : [],
                processingAddress: props.processingAddress ?? { paymentAgentDisclaimer: '', processorLines: [] },
                ageVerifyUrl: props.ageVerifyUrl ?? '',
                rtaImageUrl: props.rtaImageUrl ?? '',

                // --- Category chip rail (#custom-tags content) ---
                customTags: {
                    allTagUrl: props.customTags?.allTagUrl ?? '',
                    allTagTrackLocation: props.customTags?.allTagTrackLocation ?? 'Search Results Header',
                    allTagTrackName: props.customTags?.allTagTrackName ?? 'All',
                    arrowLeftUrl: props.customTags?.arrowLeftUrl ?? '',
                    arrowRightUrl: props.customTags?.arrowRightUrl ?? '',
                    selectedTag: props.customTags?.selectedTag ?? '---',
                    showController: Boolean(props.customTags?.showController),
                    tags: Array.isArray(props.customTags?.tags) ? props.customTags.tags : [],
                    aiPill: props.customTags?.aiPill ?? null,
                },

                pushDownAd: normalizePushDownAd(props.pushDownAd),

                hydrated: true,
            })
        },

        // Per-visit push-down config from the SPA's `pushDownAd` shared prop (see
        // HandleInertiaRequests). The copy initFromProps took from membersHeader is
        // frozen at the session's first page; eligibility and zones vary by page.
        setPushDownAd(pushDownAd) {
            this.pushDownAd = normalizePushDownAd(pushDownAd)
        },

        // --- Search autocomplete ----------------------------------------------

        /**
         * Convert math/special (monospace/bold) unicode letters to plain ASCII.
         * Local replica of master.blade.php's convertMathToPlain so we never have
         * to depend on the page global existing under the Vue flag.
         */
        convertMathToPlain(char) {
            const unicode = char.codePointAt(0)
            const ranges = [
                { start: '𝙰'.codePointAt(0), offset: 'A'.charCodeAt(0) }, // Uppercase monospace
                { start: '𝚊'.codePointAt(0), offset: 'a'.charCodeAt(0) }, // Lowercase monospace
                { start: '𝐀'.codePointAt(0), offset: 'A'.charCodeAt(0) }, // Uppercase bold
                { start: '𝐚'.codePointAt(0), offset: 'a'.charCodeAt(0) }, // Lowercase bold
            ]
            for (const range of ranges) {
                if (unicode >= range.start && unicode < range.start + 26) {
                    return String.fromCharCode(unicode - range.start + range.offset)
                }
            }
            return char
        },

        processTerm(raw) {
            const trimmed = (raw || '').trim()
            return trimmed ? [...trimmed].map(c => this.convertMathToPlain(c)).join('') : ''
        },

        /** Group raw API items the same way renderMembersDropdown() does. */
        groupItems(items) {
            const grouped = { Category: [], Site: [], Pornstar: [], AI: [] }
            items.forEach(item => {
                const type = (item.type || '').toLowerCase()
                // Checked FIRST, as develop does: 'na_ai' matches none of the substring tests
                // below, so without this branch the AI results were silently dropped.
                if (type === 'na_ai') {
                    grouped.AI.push(item)
                } else if (type.includes('category') || type.includes('fantasy') || type.includes('who') || type.includes('where')) {
                    grouped.Category.push(item)
                } else if (type.includes('site')) {
                    grouped.Site.push(item)
                } else if (type.includes('pornstar') || type.includes('performer')) {
                    grouped.Pornstar.push(item)
                }
            })
            return grouped
        },

        clearSearchResults() {
            this.searchResults = []
            this.searchGrouped = { Category: [], Site: [], Pornstar: [], AI: [] }
            this.searchActiveTerm = ''
            this.searchEmpty = false
        },

        /**
         * POST to members2.autocomplete.basic for `rawInput`. `vr` forces the VR
         * flag (mobile passes false). The stale-response guard compares the
         * processed CURRENT input against the term we requested, exactly like the
         * legacy renderMembersDropdown() before it would render.
         *
         * `currentInputGetter` returns the live input value at response time.
         */
        async fetchAutocomplete(rawInput, { vr = this.vrFilterActive, currentInputGetter = null } = {}) {
            const processed = this.processTerm(rawInput)
            if (processed.length < 2) {
                this.clearSearchResults()
                return
            }

            this.searchLastRequestTerm = processed

            try {
                const { data } = await axios.post(
                    this.autocompleteUrl,
                    { term: processed, vr: vr ? 1 : 0 },
                    {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'X-CSRF-TOKEN': this.csrfToken,
                        },
                    },
                )
                this.applyAutocompleteResponse(data, processed, vr, currentInputGetter)
            } catch (e) {
                // Mirror the legacy .fail() → just hide the dropdown.
                this.clearSearchResults()
            }
        },

        /**
         * Apply an autocomplete response. Faithful port of renderMembersDropdown:
         *   - stale guard (current processed input must equal requested term)
         *   - VR filter (item.is_vr === true) when vr active
         *   - empty + VR-empty states
         */
        applyAutocompleteResponse(items, requestedTerm, vr, currentInputGetter) {
            // Stale-response guard.
            if (typeof currentInputGetter === 'function') {
                const currentProcessed = this.processTerm(currentInputGetter())
                if (currentProcessed !== requestedTerm) {
                    return
                }
            }

            const list = Array.isArray(items) ? items : []

            if (list.length === 0) {
                // Empty: VR active → show VR-empty state; otherwise hide.
                this.searchResults = []
                this.searchGrouped = { Category: [], Site: [], Pornstar: [], AI: [] }
                this.searchActiveTerm = requestedTerm
                this.searchEmpty = !!vr
                return
            }

            let itemsToRender = list
            if (vr) {
                itemsToRender = itemsToRender.filter(item => item.is_vr === true)
            }

            if (itemsToRender.length === 0) {
                this.searchResults = []
                this.searchGrouped = { Category: [], Site: [], Pornstar: [], AI: [] }
                this.searchActiveTerm = requestedTerm
                this.searchEmpty = !!vr
                return
            }

            this.searchResults = itemsToRender
            this.searchGrouped = this.groupItems(itemsToRender)
            this.searchActiveTerm = requestedTerm
            this.searchEmpty = false
        },

        /** Build a "View All" URL for a section, mirroring buildMembersSearchViewAllUrl. */
        buildViewAllUrl(type, term, filterVR) {
            const params = new URLSearchParams()
            params.set('term', term)
            params.set('type', type)
            if (filterVR) {
                params.set('content', 'vr')
            }
            return this.searchActionUrl + '?' + params.toString()
        },

        /** Build an item destination URL (item.id is already the base URL). */
        buildItemUrl(item, term, filterVR) {
            let url = item.id + '?search_log=' + encodeURIComponent(term)
            if (filterVR) {
                url += '&content=vr'
            }
            return url
        },

        /**
         * Flag the header as running inside the live Inertia SPA shell. Called once
         * by MembersAreaLayout on mount — the only context where Inertia's router is
         * initialised. Gates submitSearch() onto the in-place client router.
         */
        markSpaActive() {
            this.spaActive = true
        },

        /**
         * Submit a search term to the search action endpoint.
         *
         * Inside the SPA shell (spaActive) route through Inertia's client router so
         * the persistent header stays mounted and only the page content swaps in
         * place — no full reload, the old page stays interactive while the request
         * is in flight, and the blue progress bar covers just the XHR + swap. The
         * backend (SearchController::inertiaRedirect under config('members.spa'))
         * returns an Inertia redirect to the category/site/performer page, and
         * Inertia::location() transparently falls back to a hard nav for any
         * non-Inertia target.
         *
         * Returns true when it handled navigation (or the term was empty, so the
         * caller should do nothing); false to let the caller fall back to a native
         * form submit (Blade-island context, where the router has no Inertia app).
         */
        submitSearch(term, { filterVR = false } = {}) {
            const trimmed = (term || '').trim()
            if (trimmed.length === 0) {
                return true // empty-guard: nothing to do, and don't native-submit
            }
            if (!this.spaActive) {
                return false
            }
            router.get(this.searchActionUrl, {
                term: trimmed,
                a_tag: 'Search Box',
                content: filterVR ? 'vr' : 'all',
            })
            return true
        },

        toggleVrFilter() {
            this.vrFilterActive = !this.vrFilterActive
        },
    },
})
